import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { favoriteKey, readFavorites, toggleFavorite, writeFavorites } from './favorites-store.js';

/**
 * Renders saved favorites as cards. Fetches each unique product handle once,
 * then maps color/variant entries onto the right image and URL.
 *
 * @extends Component<{ grid: HTMLElement, emptyState: HTMLElement, loadingState?: HTMLElement }>
 */
class FavoritesPageComponent extends Component {
  requiredRefs = ['grid', 'emptyState'];

  #currency = 'USD';
  #moneyFormat = '${{amount}}';

  /** @type {Map<string, object>} */
  #productCache = new Map();

  /** @type {Set<string>} */
  #missingHandles = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#currency = this.dataset.currency || this.#currency;
    this.#moneyFormat = this.dataset.moneyFormat || this.#moneyFormat;
    this.#render();
  }

  /**
   * @param {PointerEvent} event
   */
  removeFavorite(event) {
    const card = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('.favorites-card') : null
    );
    const handle = card?.dataset.handle;
    if (!card || !handle) return;

    const variantId = card.dataset.variantId || null;
    toggleFavorite(handle, variantId);
    card.remove();
    this.#syncEmptyState();
  }

  async #render() {
    const favorites = readFavorites();
    this.refs.grid.replaceChildren();

    if (favorites.length === 0) {
      this.#setLoading(false);
      this.#syncEmptyState();
      return;
    }

    this.#setLoading(true);

    const template = document.getElementById('favorites-page-card-template');
    if (!(template instanceof HTMLTemplateElement)) {
      this.#setLoading(false);
      return;
    }

    const uniqueHandles = [...new Set(favorites.map((entry) => entry.handle))];
    await Promise.all(uniqueHandles.map((handle) => this.#loadProduct(handle)));

    /** @type {string[]} */
    const staleKeys = [];

    for (const entry of favorites) {
      const product = this.#productCache.get(entry.handle);
      if (!product) {
        // Solo limpiar handles que ya no existen (404), no fallos de red.
        if (this.#missingHandles.has(entry.handle)) {
          staleKeys.push(favoriteKey(entry));
        }
        continue;
      }

      const variant =
        entry.variantId != null
          ? product.variants?.find((item) => String(item.id) === String(entry.variantId))
          : null;

      const content = template.content.firstElementChild;
      const card = content instanceof HTMLElement ? content.cloneNode(true) : null;
      if (!(card instanceof HTMLElement)) continue;

      card.dataset.handle = entry.handle;
      if (entry.variantId) card.dataset.variantId = entry.variantId;

      const url = entry.variantId
        ? `${product.url}${product.url.includes('?') ? '&' : '?'}variant=${entry.variantId}`
        : product.url;

      card.querySelectorAll('[data-card-link]').forEach((el) => el.setAttribute('href', url));

      const image = card.querySelector('[data-card-image]');
      if (image instanceof HTMLImageElement) {
        const imageSrc =
          variant?.featured_image?.src ||
          (typeof variant?.featured_image === 'string' ? variant.featured_image : '') ||
          product.featured_image ||
          product.images?.[0] ||
          '';
        image.src = typeof imageSrc === 'string' ? imageSrc : '';
        image.alt = product.title;
        image.loading = 'lazy';
      }

      const title = card.querySelector('[data-card-title]');
      if (title) {
        const colorLabel = this.#variantColorLabel(product, variant);
        title.textContent = colorLabel ? `${product.title} — ${colorLabel}` : product.title;
      }

      const price = card.querySelector('[data-card-price]');
      if (price) {
        const amount = variant?.price ?? product.price;
        price.textContent = formatMoney(amount, this.#moneyFormat, this.#currency);
      }

      this.refs.grid.append(card);
    }

    if (staleKeys.length) {
      writeFavorites(favorites.filter((entry) => !staleKeys.includes(favoriteKey(entry))));
    }

    this.#setLoading(false);
    this.#syncEmptyState();
  }

  /**
   * @param {string} handle
   * @returns {Promise<object | null>}
   */
  async #loadProduct(handle) {
    if (this.#missingHandles.has(handle)) return null;
    if (this.#productCache.has(handle)) {
      return this.#productCache.get(handle) ?? null;
    }

    const result = await this.#fetchProduct(handle, 2);
    if (result.status === 'ok') {
      this.#productCache.set(handle, result.product);
      return result.product;
    }
    if (result.status === 'missing') {
      this.#missingHandles.add(handle);
    }
    return null;
  }

  /**
   * @param {string} handle
   * @param {number} attempts
   * @returns {Promise<{ status: 'ok', product: object } | { status: 'missing' } | { status: 'error' }>}
   */
  async #fetchProduct(handle, attempts) {
    let sawNotFound = false;

    for (let i = 0; i < attempts; i += 1) {
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (response.status === 404) {
          sawNotFound = true;
          break;
        }
        if (!response.ok) continue;
        const product = await response.json();
        return { status: 'ok', product };
      } catch {
        // retry
      }
    }

    return { status: sawNotFound ? 'missing' : 'error' };
  }

  /**
   * @param {object} product
   * @param {object | null | undefined} variant
   */
  #variantColorLabel(product, variant) {
    if (!variant || !product?.options) return '';

    const options = product.options;
    let colorIndex = -1;
    for (let i = 0; i < options.length; i += 1) {
      const name = String(options[i] || '').toLowerCase();
      if (name.includes('color') || name.includes('colour') || name.includes('tono')) {
        colorIndex = i;
        break;
      }
    }

    if (colorIndex === -1) return variant.option1 || '';
    return variant[`option${colorIndex + 1}`] || '';
  }

  /** @param {boolean} isLoading */
  #setLoading(isLoading) {
    const loading = this.refs.loadingState;
    if (loading instanceof HTMLElement) {
      loading.hidden = !isLoading;
    }
  }

  #syncEmptyState() {
    const hasCards = this.refs.grid.children.length > 0;
    this.refs.emptyState.hidden = hasCards;
    const loading = this.refs.loadingState;
    if (loading instanceof HTMLElement && hasCards) {
      loading.hidden = true;
    }
  }
}

if (!customElements.get('favorites-page-component')) {
  customElements.define('favorites-page-component', FavoritesPageComponent);
}
